/**
 * 持仓相关 localStorage 按登录用户隔离，避免同浏览器换号串数据。
 *
 * 约定：
 * - 本地键：`${baseKey}_u_${userId}`；未登录用 `${baseKey}__logged_out`
 * - 云端备份包内仍用规范键名（holding_store_v1 等），不带用户后缀
 * - 旧全局键 `asset_center_holding_store_v1`：仅首次有登录用户且其分桶为空时认领一次
 */

export const HOLDING_STORE_BASE_KEY = 'asset_center_holding_store_v1';
export const SYNC_META_BASE_KEY = 'asset_holding_cloud_sync_meta_v1';
export const AUTO_BACKUP_LOCAL_BASE_KEY = 'asset_auto_backup_local_v1';
export const HIDE_MONEY_BASE_KEY = 'asset_center_hide_money_v1';
export const HUB_REFRESH_BASE_KEY = 'asset_hub_refresh_state_v1';
export const REDEEM_ACK_BASE_KEY = 'redeemRemindAck_v1';
export const MP_OPENID_BASE_KEY = 'asset_center_mp_openid_v1';

const ACTIVE_USER_KEY = 'asset_storage_active_user_v1';
const LEGACY_CLAIM_KEY = 'asset_center_legacy_store_claimed_v1';

const LEGACY_KEYS_TO_CLAIM = [
  HOLDING_STORE_BASE_KEY,
  SYNC_META_BASE_KEY,
  AUTO_BACKUP_LOCAL_BASE_KEY,
  HIDE_MONEY_BASE_KEY,
  HUB_REFRESH_BASE_KEY,
  REDEEM_ACK_BASE_KEY,
  'asset_center_migrate_shares_6_done_v1',
  'asset_center_prune_empty_done_v1',
  MP_OPENID_BASE_KEY
];

function readUserData() {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

/** 当前登录用户 id；未登录返回 null */
export function getAssetStorageUserId() {
  const u = readUserData();
  const id = u?.id ?? u?.userId;
  if (id == null || String(id).trim() === '') return null;
  return String(id);
}

export function scopedAssetKey(baseKey) {
  const uid = getAssetStorageUserId();
  if (!uid) return `${baseKey}__logged_out`;
  return `${baseKey}_u_${uid}`;
}

function getActiveStorageUserId() {
  try {
    return localStorage.getItem(ACTIVE_USER_KEY) || '';
  } catch (_) {
    return '';
  }
}

function setActiveStorageUserId(userId) {
  try {
    if (userId) localStorage.setItem(ACTIVE_USER_KEY, String(userId));
    else localStorage.removeItem(ACTIVE_USER_KEY);
  } catch (_) {
    // ignore
  }
}

/**
 * 把旧「全局」持仓键认领到当前用户分桶（仅一次，且仅当目标分桶为空）。
 * 避免换号后 B 继续读到 A 的全局数据；第二个用户不会再领走已认领的全局包。
 */
export function migrateLegacyHoldingStoreForUser(userId) {
  if (!userId) return { migrated: false };
  const uid = String(userId);

  try {
    const claimed = localStorage.getItem(LEGACY_CLAIM_KEY);
    if (claimed && claimed !== uid) {
      return { migrated: false, reason: 'claimed_by_other' };
    }

    const scopedStoreKey = `${HOLDING_STORE_BASE_KEY}_u_${uid}`;
    const existingScoped = localStorage.getItem(scopedStoreKey);
    if (existingScoped) {
      // 分桶已有数据：若全局键还在且尚未认领，直接丢掉全局键，防止串读
      if (!claimed && localStorage.getItem(HOLDING_STORE_BASE_KEY)) {
        LEGACY_KEYS_TO_CLAIM.forEach((k) => {
          try { localStorage.removeItem(k); } catch (_) { /* ignore */ }
        });
        localStorage.setItem(LEGACY_CLAIM_KEY, uid);
      }
      return { migrated: false, reason: 'scoped_exists' };
    }

    const legacyRaw = localStorage.getItem(HOLDING_STORE_BASE_KEY);
    if (!legacyRaw) {
      return { migrated: false, reason: 'no_legacy' };
    }

    // 认领：拷贝到分桶，再删除全局键
    LEGACY_KEYS_TO_CLAIM.forEach((base) => {
      const legacyVal = localStorage.getItem(base);
      if (legacyVal == null) return;
      const scoped = `${base}_u_${uid}`;
      if (!localStorage.getItem(scoped)) {
        localStorage.setItem(scoped, legacyVal);
      }
      localStorage.removeItem(base);
    });
    localStorage.setItem(LEGACY_CLAIM_KEY, uid);
    return { migrated: true };
  } catch (e) {
    console.warn('[assetStorageScope] legacy migrate failed:', e);
    return { migrated: false, reason: 'error' };
  }
}

function clearDerivedCachesAllUsers() {
  try {
    const prefixes = [
      'asset_profit_day_cache_v1_',
      'asset_trend_nav_cache_v1_'
    ];
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (prefixes.some((p) => key.startsWith(p))) {
        localStorage.removeItem(key);
      }
    }
  } catch (_) {
    // ignore
  }
}

/**
 * 无法安全归属的旧全局持仓：挪到隔离键，避免被下一个登录用户认领后覆盖。
 */
function quarantineUnclaimedLegacyStore() {
  try {
    const legacyRaw = localStorage.getItem(HOLDING_STORE_BASE_KEY);
    if (!legacyRaw) return;
    const quarantineKey = `${HOLDING_STORE_BASE_KEY}__quarantine`;
    if (!localStorage.getItem(quarantineKey)) {
      localStorage.setItem(quarantineKey, legacyRaw);
    }
    LEGACY_KEYS_TO_CLAIM.forEach((k) => {
      try { localStorage.removeItem(k); } catch (_) { /* ignore */ }
    });
  } catch (_) {
    // ignore
  }
}

/**
 * 登录成功 / 登出 / 会话恢复后调用。
 * user: 规范化后的用户对象；登出传 null。
 * 须在写入/清除 userData 之后调用。
 * options.leavingUserId: 登出前已知的用户 id（userData 已清时仍能把旧全局键归给离开的用户）
 */
export function onAuthUserChangedForAssetStorage(user, options = {}) {
  const nextId = user?.id != null && String(user.id).trim() !== ''
    ? String(user.id)
    : null;
  const prevId = getActiveStorageUserId()
    || (options.leavingUserId != null && String(options.leavingUserId).trim() !== ''
      ? String(options.leavingUserId)
      : '');
  const switched = String(prevId || '') !== String(nextId || '');

  // 离开某用户时：先把尚未认领的全局持仓归给离开的用户，防止被下一个账号领走后「从腾讯云恢复」盖掉
  if (switched && prevId) {
    migrateLegacyHoldingStoreForUser(prevId);
    if (nextId && nextId !== prevId && localStorage.getItem(HOLDING_STORE_BASE_KEY)) {
      quarantineUnclaimedLegacyStore();
    }
  }

  if (nextId) {
    migrateLegacyHoldingStoreForUser(nextId);
  }

  if (switched) {
    try {
      // 延迟引用，避免与 cloudSync 循环依赖在模块初始化阶段炸开
      const sync = require('./assetHoldingCloudSync');
      if (typeof sync.resetCloudSyncRuntimeForUserSwitch === 'function') {
        sync.resetCloudSyncRuntimeForUserSwitch();
      } else if (typeof sync.clearSessionSyncPassword === 'function') {
        sync.clearSessionSyncPassword();
      }
    } catch (_) {
      // ignore
    }
    clearDerivedCachesAllUsers();
  }

  setActiveStorageUserId(nextId);

  if (switched) {
    try {
      // 只发 user-changed：勿发 asset-holdings-changed，否则云同步会把新用户空本地标 dirty 并误推云端
      window.dispatchEvent(new CustomEvent('asset-storage-user-changed', {
        detail: { previousUserId: prevId || null, nextUserId: nextId }
      }));
    } catch (_) {
      // ignore
    }
  }
}
